import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '/app/environments/environment';


interface SetMonitorsPayload {
  name: string;
  description: string;
  ruleId: null;
  clientId: string;
  eventColors: object;
  allowedUserIds: string[];
  corporateId: string;
  riskEvaluation: boolean;
}


/**
 * MonitorsService
 *
 * Servicio Angular responsable de gestionar las operaciones relacionadas con monitores.
 * Proporciona métodos para interactuar con la API backend relacionada con monitores.
 *
 * @Injectable Decorador que marca la clase como disponible para inyección de dependencias
 * @providedIn 'root' Indica que el servicio debe ser proporcionado en el inyector raíz
 */
@Injectable({
  providedIn: 'root',
})
export class MonitorsService {
  /**
   * Subject para notificar actualizaciones de monitores a otros componentes
   */
  public monitorUpdated: Subject<any> = new Subject<any>();

  /**
   * Constructor del servicio MonitorsService
   *
   * @param httpClient Instancia de HttpClient para realizar peticiones HTTP
   */
  constructor(private httpClient: HttpClient) { }

  /**
   * Obtiene la lista de monitores desde el servidor
   *
   * Realiza una petición GET al endpoint de monitores configurado en el environment.
   *
   * @returns Observable<any> Observable que emite la respuesta HTTP con la lista de monitores
   *
  * @example
  * // Uso típico del método
  * // this.monitorsService.getMonitors().subscribe({ next: (monitors) => { ... }, error: (err) => console.error(err) });
  *
   * @throws Error cuando la petición HTTP falla o el servidor retorna un error
   */
  public getMonitors(): Observable<any> {
    return this.httpClient.get(environment.api + '/monitors');
  }

  public getMonitor(idMonitor: string): Observable<any> {
    return this.httpClient.get(`${environment.api}/monitors/${idMonitor}`);
  }

  public getMetrics(options?: {
    granularity?: string;
    startDate?: string;
    endDate?: string;
    monitorId?: string;
  }): Observable<any> {
    let params = new HttpParams();

    if (options) {
      if (options.granularity) {
        params = params.set('granularity', options.granularity);
      }
      if (options.startDate) {
        const startDate = new Date(options.startDate);
        params = params.set('startDate', startDate.toISOString());
      }
      if (options.endDate) {
        const endDate = new Date(options.endDate);
        params = params.set('endDate', endDate.toISOString());
      }
      if (options.monitorId) {
        params = params.set('monitorId', options.monitorId);
      }
    } else {
      // default granularity
      params = params.set('granularity', 'TODAY');
    }

    return this.httpClient.get(`${environment.api}/dashboard/metrics`, {
      params,
    });
  }

  public updateMonitor(monitor: any): Observable<any> {
    return this.httpClient.put(
      `${environment.api}/monitors/${monitor.id}`,
      monitor
    );
  }


  public setMonitors(payload: SetMonitorsPayload): Observable<any> {
    return this.httpClient.post(environment.api + "/monitors", payload);
  }



}
